import { useEffect, useState } from "react";

import { resolvePlace, saveProperty } from "../api/client";
import type { ScoreResponse } from "../types/api";
import { ChatPanel } from "./ChatPanel";
import { CostToOwnPanel } from "./CostToOwnPanel";
import { Disclaimer } from "./Disclaimer";
import { FlipRiskPanel } from "./FlipRiskPanel";
import { NarrativeBrief } from "./NarrativeBrief";
import { ExteriorViewLink } from "./ExteriorViewCard";
import { PropertyMap } from "./PropertyMap";
import { PropertySummaryCard } from "./PropertySummaryCard";
import { ProScorePanel } from "./ProScorePanel";
import { FactorCardView } from "./ScoreResult";
import { ShareLink } from "./ShareLink";

/**
 * PaidPropertyView — Task 9.7a Pass B.
 *
 * Evolves the free look-and-feel into the Pro surface. Vertical flow:
 *   action row (New search · Save)
 *   → At-A-Glance + map
 *   → Summary tile: Tilt Map + offer range (editable price) + confidence,
 *     plus the same data grid as free, extended with AVM / flood / permits
 *   → Pro tiles: the 5 factor cards, full width
 *   → side-by-side: chat (primary) | "What the data shows" narrative
 *   → carrying cost + flip watch (full width)
 *   → share · action row (New search · Save)
 *
 * The data leads (it's what the user paid for); chat supports. Reuses the
 * free At-A-Glance, summary card, and factor cards so it reads as "same app,
 * more" rather than a new UI.
 */

interface Props {
  /** null while the score is still computing — renders the skeleton shell. */
  result: ScoreResponse | null;
  placeId: string | null;
  address: string;
  askingPrice: number;
  onNewSearch: () => void;
}

/** Shimmer placeholder used while the verified panels are still scoring. */
function Skel({ variant }: { variant?: "line" | "line-short" | "hero" | "tile" }) {
  return <span className={`pt-skel pt-skel-${variant ?? "line"}`} aria-hidden />;
}

// Starter chips for the Pro chat: the familiar free prompts plus a few that
// exercise licensed ATTOM data the free tier can't answer.
const PAID_SUGGESTIONS = [
  "What's the negotiation room here?",
  "Any red flags I should know about?",
  "What should I offer?",
  "What's the owner's equity position?",
  "Has the AVM been trending up or down?",
  "Any building permits on record?",
];

function SaveButton({
  placeId,
  address,
  askingPrice,
}: {
  placeId: string;
  address: string;
  askingPrice: number;
}) {
  const [state, setState] = useState<"idle" | "saving" | "saved" | "error">("idle");
  async function handleSave() {
    setState("saving");
    try {
      // Save the asking price too so the saved-list link can re-score directly.
      await saveProperty(placeId, address, undefined, askingPrice);
      setState("saved");
    } catch {
      setState("error");
    }
  }
  if (state === "saved") return <span className="saved-confirm">✓ Saved</span>;
  return (
    <button
      type="button"
      className="save-property-btn"
      onClick={() => void handleSave()}
      disabled={state === "saving"}
    >
      {state === "saving" ? "Saving…" : state === "error" ? "Retry save" : "★ Save property"}
    </button>
  );
}

function ActionRow({
  placeId,
  address,
  askingPrice,
  onNewSearch,
}: {
  placeId: string | null;
  address: string;
  askingPrice: number;
  onNewSearch: () => void;
}) {
  return (
    <div className="paid-action-row">
      <button type="button" className="new-search" onClick={onNewSearch}>
        ← New search
      </button>
      {placeId && <SaveButton placeId={placeId} address={address} askingPrice={askingPrice} />}
    </div>
  );
}

export function PaidPropertyView({ result, placeId, address, askingPrice, onNewSearch }: Props) {
  const [coords, setCoords] = useState<{ lat: number; lon: number } | null>(null);
  // The analyst replaces these fixed chips with property-specific follow-ups
  // once the brief finishes (pro-ai-analyst Task 10 — NarrativeBrief reports
  // them via onSuggestions).
  const [suggestions, setSuggestions] = useState<string[]>(PAID_SUGGESTIONS);

  useEffect(() => {
    if (!placeId) return;
    let cancelled = false;
    resolvePlace(placeId)
      .then((p) => {
        if (!cancelled) setCoords({ lat: p.lat, lon: p.lon });
      })
      .catch(() => {
        /* map falls back to placeholder */
      });
    return () => {
      cancelled = true;
    };
  }, [placeId]);

  // Active Listing facts — all licensed (property-facts Task 5). On Pro these
  // replace the free tier's web-search specs, so the card needs no web search.
  // Null while the score is still computing → the card shows skeleton rows.
  const proExtras = {
    beds: result?.beds ?? null,
    baths: result?.baths ?? null,
    sqft: result?.living_sqft ?? null,
    pricePerSqft: result?.living_sqft ? askingPrice / result.living_sqft : null,
    yearBuilt: result?.year_built ?? null,
    lotSizeAcres: result?.lot_size_acres ?? null,
    lastSalePrice: result?.last_sale_price ?? null,
    lastSaleDate: result?.last_sale_date ?? null,
    avmValue: result?.avm_value ?? null,
    avmChangePct: result?.avm_12mo_change_pct ?? null,
    avmConfidence: result?.avm_confidence ?? null,
    floodZone: result?.flood_zone ?? null,
    equityPct: result?.equity_pct ?? null,
    ownerOccupied: result?.owner_occupied ?? null,
    annualTax: result?.annual_tax ?? null,
    assessedValue: result?.assessed_value ?? null,
    marketValue: result?.market_value ?? null,
    permitCount: result?.recent_permit_count ?? null,
    nriHazards: result?.nri_hazards ?? null,
    crimeIndex: result?.crime_index ?? null,
    airQualityIndex: result?.air_quality_index ?? null,
    ownerOccupancyPct: result?.owner_occupancy_pct ?? null,
    vacancyPct: result?.vacancy_pct ?? null,
    medianCommuteMin: result?.median_commute_min ?? null,
    medianHouseholdIncome: result?.median_household_income ?? null,
    bachelorsPlusPct: result?.bachelors_plus_pct ?? null,
    walkScore: result?.walk_score ?? null,
    soundscore: result?.soundscore ?? null,
  };

  return (
    <article className="paid-property-view">
      <ActionRow placeId={placeId} address={address} askingPrice={askingPrice} onNewSearch={onNewSearch} />

      <section className="at-a-glance" aria-label="Property at a glance">
        <div className="address-row">
          <h2 className="property-address">{address}</h2>
          {/* Curb photo (curb-photo): inline Street View trigger by the address,
              opens a modal — never embedded beside the Amazon Location map. */}
          {placeId && <ExteriorViewLink placeId={placeId} />}
        </div>
        {coords ? (
          <PropertyMap lat={coords.lat} lon={coords.lon} address={address} />
        ) : (
          <div className="property-map-placeholder" role="img" aria-label="Map loading">
            <span>Map…</span>
          </div>
        )}
      </section>

      {/* Summary tile: hero score + offer range + the extended data grid. */}
      <div className="pro-summary-tile">
        {result ? (
          <ProScorePanel
            scoreId={result.score_id}
            offerRange={result.offer_range}
            tiltMap={result.tilt_map}
            confidence={result.confidence}
            initialAskingPrice={askingPrice}
            address={result.address}
          />
        ) : (
          <div className="pro-score-panel pro-score-panel-loading" aria-live="polite">
            <Skel variant="hero" />
            <div className="pro-score-loading-msg">
              Scoring your verified Tilt Map…
            </div>
          </div>
        )}
        {placeId && (
          <PropertySummaryCard
            placeId={placeId}
            address={address}
            pro={proExtras}
            proLoading={!result}
          />
        )}
      </div>

      {/* Pro tiles — the factor cards (derived intelligence / Tilt Map
          breakdown). Raw facts like Permits live in the Active Listing window
          above, not here (property-facts Task 5 — tiles are insights, the
          window is facts). */}
      <section className="pro-tiles" aria-label="Factor breakdown">
        <div className="factor-grid">
          {result ? (
            result.factors.map((f) => <FactorCardView key={f.name} factor={f} />)
          ) : (
            Array.from({ length: 5 }).map((_, i) => (
              <div className="factor-card direction-neutral" key={i}>
                <Skel variant="line-short" />
                <Skel variant="line" />
                <Skel variant="line" />
              </div>
            ))
          )}
        </div>
      </section>

      {/* Verified narrative — the Pro AI Analyst streams it below the score
          (pro-ai-analyst Task 10). It investigates, streams the brief, then
          feeds property-specific follow-up questions to the chat chips below. */}
      {result ? (
        <NarrativeBrief
          scoreId={result.score_id}
          narrativeUrl={result.narrative_url}
          initialNarrative={result.narrative}
          onSuggestions={setSuggestions}
        />
      ) : (
        <section className="pro-narrative" aria-label="What the data shows">
          <h3>What the data shows</h3>
          <div className="pro-narrative-loading">
            <Skel variant="line" />
            <Skel variant="line" />
            <Skel variant="line-short" />
          </div>
        </section>
      )}

      {/* Ask a follow-up — placed directly under the brief so the conversation
          continues the analyst's analysis (per John, 2026-06-13). Starter chips
          are the analyst's property-specific suggestions (NarrativeBrief sets
          them via onSuggestions) until then the Pro defaults. */}
      {result && (
        <section className="pro-chat" aria-label="Ask a follow-up question">
          <ChatPanel
            scoreId={result.score_id}
            initialHistory={result.chat_history ?? []}
            suggestions={suggestions}
          />
        </section>
      )}

      {result?.flip_risk && <FlipRiskPanel risk={result.flip_risk} />}

      {/* Cost to own — the calculator sits LAST (per John, 2026-06-13), after
          the brief, chat, and flip watch. Needs the closing-cost estimate from
          /score. */}
      {result ? (
        <CostToOwnPanel askingPrice={askingPrice} closingCost={result.closing_cost} scoreId={result.score_id} />
      ) : (
        <section className="cost-to-own" aria-label="Estimated monthly cost to own">
          <h3>Estimated monthly cost to own</h3>
          <Skel variant="line" />
          <Skel variant="line-short" />
        </section>
      )}

      {result && <ShareLink scoreId={result.score_id} />}

      <ActionRow placeId={placeId} address={address} askingPrice={askingPrice} onNewSearch={onNewSearch} />

      <Disclaimer />
    </article>
  );
}
